import type {
  SimpleDateDTO,
  VacationDTO,
  VacationDataDTO,
  WorkdayDTO,
} from "@wkt/api/checkin/checkinDTO";
import { DayType } from "@wkt/api/checkin/checkinENUM";
import { parseSimpleDateValue } from "@wkt/composables/useSimpleDate";
import { useCheckinStore } from "@wkt/stores/checkin";
import { useSessionCatalogStore } from "@wkt/stores/sessionCatalog";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const COMPLETE_DAY_RECORD_TYPE = 7;
const HOURS_BAG_VACATION_ID = 6;
export const HOURS_BAG_UNIT_DAYS = 0;
export const HOURS_BAG_UNIT_HOURS = 1;

export function getBagUnit(
  company:
    | {
        defaultYearlyExtraUnit?: number | null;
        defaultYearlyHoursBag1Unit?: number | null;
        defaultYearlyHoursBag2Unit?: number | null;
        defaultYearlyHoursBag3Unit?: number | null;
        defaultYearlyHoursBag4Unit?: number | null;
        defaultYearlyHoursBag5Unit?: number | null;
      }
    | null
    | undefined,
  hoursBagType?: number | null,
): number {
  if (hoursBagType == null || hoursBagType === HOURS_BAG_VACATION_ID) {
    return HOURS_BAG_UNIT_DAYS;
  }

  if (!company) {
    return HOURS_BAG_UNIT_DAYS;
  }

  switch (hoursBagType) {
    case 7:
      return company.defaultYearlyExtraUnit ?? HOURS_BAG_UNIT_DAYS;
    case 1:
      return company.defaultYearlyHoursBag1Unit ?? HOURS_BAG_UNIT_DAYS;
    case 2:
      return company.defaultYearlyHoursBag2Unit ?? HOURS_BAG_UNIT_DAYS;
    case 3:
      return company.defaultYearlyHoursBag3Unit ?? HOURS_BAG_UNIT_DAYS;
    case 4:
      return company.defaultYearlyHoursBag4Unit ?? HOURS_BAG_UNIT_DAYS;
    case 5:
      return company.defaultYearlyHoursBag5Unit ?? HOURS_BAG_UNIT_DAYS;
    default:
      return HOURS_BAG_UNIT_DAYS;
  }
}

export function isHoursBagUnit(
  company:
    | {
        defaultYearlyExtraUnit?: number | null;
        defaultYearlyHoursBag1Unit?: number | null;
        defaultYearlyHoursBag2Unit?: number | null;
        defaultYearlyHoursBag3Unit?: number | null;
        defaultYearlyHoursBag4Unit?: number | null;
        defaultYearlyHoursBag5Unit?: number | null;
      }
    | null
    | undefined,
  hoursBagType?: number | null,
): boolean {
  return getBagUnit(company, hoursBagType) === HOURS_BAG_UNIT_HOURS;
}

type DayChipTone =
  | "accent"
  | "neutral"
  | "warning"
  | "vacation"
  | "paid-absence"
  | "unpaid-absence"
  | "rest"
  | "holiday";

interface DayChip {
  key: string;
  label: string;
  tone: DayChipTone;
}

interface VacationDateLike {
  year?: number | null;
  month?: number | null;
  day?: number | null;
}

interface VacationRecordLike {
  recordDateTs?: number | null;
  fromDate?: VacationDateLike | null;
  toDate?: VacationDateLike | null;
}

const parseCalendarDay = parseSimpleDateValue;

function compareSimpleDates(
  left?: SimpleDateDTO | null,
  right?: SimpleDateDTO | null,
): number {
  if (!left || !right) {
    return 0;
  }

  const leftTime = new Date(left.year, left.month - 1, left.day).getTime();
  const rightTime = new Date(right.year, right.month - 1, right.day).getTime();
  return leftTime - rightTime;
}

function toSortableDateValue(date?: VacationDateLike | null): number {
  if (!date?.year || !date?.month || !date?.day) {
    return Number.NEGATIVE_INFINITY;
  }

  return new Date(date.year, date.month - 1, date.day).getTime();
}

function getVacationRecordRecencyValue(record?: VacationRecordLike | null): number {
  if (!record) {
    return Number.NEGATIVE_INFINITY;
  }

  return Math.max(
    typeof record.recordDateTs === "number"
      ? record.recordDateTs
      : Number.NEGATIVE_INFINITY,
    toSortableDateValue(record.toDate),
    toSortableDateValue(record.fromDate),
  );
}

export function getMostRecentVacationRecord<T extends VacationRecordLike>(
  records: T[],
): T | null {
  let latestRecord: T | null = null;
  let latestValue = Number.NEGATIVE_INFINITY;

  for (const record of records) {
    const currentValue = getVacationRecordRecencyValue(record);
    if (currentValue > latestValue) {
      latestRecord = record;
      latestValue = currentValue;
    }
  }

  return latestRecord;
}

function isDateWithinVacation(
  date: SimpleDateDTO | null,
  vacation?: VacationDTO | null,
): boolean {
  if (!date || !vacation?.fromDate || !vacation?.toDate) {
    return false;
  }

  return (
    compareSimpleDates(date, vacation.fromDate) >= 0 &&
    compareSimpleDates(date, vacation.toDate) <= 0
  );
}

export interface VacationWorkdayLike {
  closed?: boolean | null;
  currentActivityStart?: string | null;
  totalActivityWorkHours?: number | null;
  totalActivityNonWorkHours?: number | null;
  activityList?: unknown[] | null;
  workdayDate?: SimpleDateDTO | null;
}

export function hasCompleteDayLeaveOnDate(
  date: SimpleDateDTO | null | undefined,
  vacations: VacationDTO[] | null | undefined,
  recordType = COMPLETE_DAY_RECORD_TYPE,
): boolean {
  if (!date) {
    return false;
  }

  return (
    vacations?.some(
      (vacation) =>
        vacation?.recordType === recordType &&
        isDateWithinVacation(date, vacation),
    ) ?? false
  );
}

export function workdayHasRecordedActivity(
  workday: VacationWorkdayLike | null | undefined,
): boolean {
  if (!workday) {
    return false;
  }

  if (workday.currentActivityStart != null) {
    return true;
  }

  if ((workday.totalActivityWorkHours ?? 0) > 0) {
    return true;
  }

  if ((workday.totalActivityNonWorkHours ?? 0) > 0) {
    return true;
  }

  return Array.isArray(workday.activityList) && workday.activityList.length > 0;
}

/** Open workday on a full-day leave date that still has fichajes to resolve. */
export function isVacationWorkdayConflict(
  workday: VacationWorkdayLike | null | undefined,
  vacations: VacationDTO[] | null | undefined,
): boolean {
  if (!workday || workday.closed === true) {
    return false;
  }

  if (!hasCompleteDayLeaveOnDate(workday.workdayDate, vacations)) {
    return false;
  }

  return workdayHasRecordedActivity(workday);
}

/** Drop backend shells for leave days with no employee fichajes (e.g. after confirming Friday). */
export function suppressEmptyLeaveDayWorkday<T extends VacationWorkdayLike>(
  workday: T | null | undefined,
  vacations: VacationDTO[] | null | undefined,
): T | null {
  if (!workday || workday.closed === true) {
    return workday ?? null;
  }

  if (!hasCompleteDayLeaveOnDate(workday.workdayDate, vacations)) {
    return workday;
  }

  return workdayHasRecordedActivity(workday) ? workday : null;
}

export function normalizeWorkdayAfterVacationRefresh(
  workday: WorkdayDTO | null | undefined,
  vacationData: VacationDataDTO | null | undefined,
): WorkdayDTO | null {
  return suppressEmptyLeaveDayWorkday(
    workday ?? null,
    vacationData?.vacationData,
  );
}

export function useVacationHelpers() {
  const { t } = useI18n();
  const checkinStore = useCheckinStore();
  const sessionCatalogStore = useSessionCatalogStore();

  const currentDay = computed<SimpleDateDTO | null>(() => {
    if (checkinStore.currentWorkday?.workdayDate) {
      return checkinStore.currentWorkday.workdayDate;
    }

    return parseCalendarDay(checkinStore.todayCalendar?.calendarDay);
  });

  function getVacationByType(
    date = currentDay.value,
    recordType = COMPLETE_DAY_RECORD_TYPE,
  ): VacationDTO | null {
    return (
      checkinStore.vacationData?.vacationData?.find(
        (vacation) =>
          vacation?.recordType === recordType &&
          isDateWithinVacation(date, vacation),
      ) ?? null
    );
  }

  function getVacationByTypeBag(
    date = currentDay.value,
    recordType = COMPLETE_DAY_RECORD_TYPE,
    hoursBagType?: number | null,
  ): VacationDTO | null {
    return (
      checkinStore.vacationData?.vacationData?.find(
        (vacation) =>
          vacation?.recordType === recordType &&
          vacation?.hoursBagType === hoursBagType &&
          isDateWithinVacation(date, vacation),
      ) ?? null
    );
  }

  function hasLeave(
    date = currentDay.value,
    recordType = COMPLETE_DAY_RECORD_TYPE,
  ): boolean {
    return getVacationByType(date, recordType) != null;
  }

  function getHoursBagLabel(hoursBagType: number): string {
    const company = sessionCatalogStore.company;
    if (!company) {
      return String(hoursBagType);
    }

    switch (hoursBagType) {
      case HOURS_BAG_VACATION_ID:
        return (
          company.defaultYearlyVacationLabel ||
          t("wkt.client.common.check-card.chips.vacation")
        );
      case 7:
        return company.defaultYearlyExtraLabel || String(hoursBagType);
      case 1:
        return company.defaultYearlyHoursBag1Label || String(hoursBagType);
      case 2:
        return company.defaultYearlyHoursBag2Label || String(hoursBagType);
      case 3:
        return company.defaultYearlyHoursBag3Label || String(hoursBagType);
      case 4:
        return company.defaultYearlyHoursBag4Label || String(hoursBagType);
      case 5:
        return company.defaultYearlyHoursBag5Label || String(hoursBagType);
      default:
        return String(hoursBagType);
    }
  }

  function getCompleteDayLeaveLabel(
    date = currentDay.value,
  ): string | null {
    const paidAbsence = getVacationByTypeBag(
      date,
      COMPLETE_DAY_RECORD_TYPE,
      null,
    );
    if (paidAbsence) {
      return (
        paidAbsence.recordTitle ||
        t("wkt.client.common.check-card.chips.absence_paid")
      );
    }

    const vacation = getVacationByTypeBag(
      date,
      COMPLETE_DAY_RECORD_TYPE,
      HOURS_BAG_VACATION_ID,
    );
    if (vacation) {
      return vacation.recordTitle || getHoursBagLabel(HOURS_BAG_VACATION_ID);
    }

    const leave = getVacationByType(date, COMPLETE_DAY_RECORD_TYPE);
    if (!leave) {
      return null;
    }

    return leave.hoursBagType != null
      ? getHoursBagLabel(leave.hoursBagType)
      : leave.recordTitle || t("wkt.client.common.check-card.chips.leave");
  }

  function dayChips(date = currentDay.value): DayChip[] {
    const chips: DayChip[] = [];
    const currentCalendar =
      checkinStore.getCalendarForDate(date) ?? checkinStore.todayCalendar;
    const dayType = currentCalendar?.dayType ?? DayType.REGULAR;

    if (dayType === DayType.REST) {
      chips.push({
        key: "rest",
        label: t("wkt.client.common.check-card.chips.rest"),
        tone: "rest",
      });
    }

    if (dayType === DayType.NATIONAL_HOLIDAY) {
      chips.push({
        key: "national-holiday",
        label: t("wkt.client.common.check-card.chips.holiday"),
        tone: "holiday",
      });
    }

    if (dayType === DayType.LOCAL_HOLIDAY) {
      chips.push({
        key: "local-holiday",
        label: t("wkt.client.common.check-card.chips.local-holiday"),
        tone: "holiday",
      });
    }

    if (dayType === DayType.COMPANY_HOLIDAY) {
      chips.push({
        key: "company-holiday",
        label: t("wkt.client.common.check-card.chips.free"),
        tone: "holiday",
      });
    }

    const paidAbsence = getVacationByTypeBag(
      date,
      COMPLETE_DAY_RECORD_TYPE,
      null,
    );
    if (paidAbsence) {
      chips.push({
        key: "absence-paid",
        label:
          getCompleteDayLeaveLabel(date) ||
          t("wkt.client.common.check-card.chips.absence_paid"),
        tone: "paid-absence",
      });
    }

    const vacation = getVacationByTypeBag(
      date,
      COMPLETE_DAY_RECORD_TYPE,
      HOURS_BAG_VACATION_ID,
    );
    if (vacation) {
      chips.push({
        key: "vacation",
        label: getCompleteDayLeaveLabel(date) || getHoursBagLabel(HOURS_BAG_VACATION_ID),
        tone: "vacation",
      });
    }

    const leave = getVacationByType(date, COMPLETE_DAY_RECORD_TYPE);
    if (leave && !paidAbsence && !vacation) {
      chips.push({
        key: "leave",
        label:
          getCompleteDayLeaveLabel(date) ||
          t("wkt.client.common.check-card.chips.leave"),
        tone: "unpaid-absence",
      });
    }

    const checkinCalendar = currentCalendar as {
      checkinAllowed?: number | null;
      isCheckinAllowed?: number | null;
    } | null;
    const checkinAllowed =
      checkinCalendar?.checkinAllowed ?? checkinCalendar?.isCheckinAllowed ?? 1;
    if (checkinAllowed === 0) {
      chips.push({
        key: "no-checkin",
        label: t("wkt.client.common.check-card.chips.no_checkin"),
        tone: "warning",
      });
    }

    return chips;
  }

  const currentDayChips = computed(() => dayChips());

  return {
    currentDay,
    currentDayChips,
    hasLeave,
    getCompleteDayLeaveLabel,
    getVacationByType,
    getVacationByTypeBag,
    dayChips,
  };
}
